import React, { useEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import { ChevronDown, X } from 'lucide-react'
import logo from '../../../assets/icons/logo2.svg'
import menu from '../../../assets/icons/menu.svg'
import ServicesDropdown from './ServicesDropdown'
import AboutDropDown from './AboutDropDown'
import JoinDropDown from './JoinDropDown'
import { motion, AnimatePresence } from 'framer-motion'
import StaffDropDown from './StaffDropdown'

const ABOUT_LINKS = [
  { label: "About Us",                   path: "/about" },
  { label: "Blog",                       path: "/blog" },
  { label: "Our Team",                   path: "/our-team" },
  { label: "Life at Hifah Technologies", path: "/life-at-hifah-technologies" },
];
const SERVICES_CORE = [
  { label: "App Development",   path: "/services/app-development" },
  { label: "Web Development",   path: "/services/web-development" },
  { label: "UI UX Designing",   path: "/services/ui-ux-designing" },
  { label: "Digital Marketing", path: "/services/digital-marketing" },
  { label: "Video Editing",     path: "/services/video-editing" },
  { label: "AI Solutions",      path: "/services/ai-solutions" },
];
const CAREERS_LINKS = [
  { label: "Careers",      path: "/careers" },
  { label: "Join as Team", path: "/join-as-team" },
];
const STAFF_LINKS = [
  { label: "Staff Augmentation", path: "/staff-augmentation" },
  { label: "Hire Developers",    path: "/hire-developers" },
  { label: "Our Team",           path: "/our-team" },
];

const MobileAccordion = ({ id, label, links, closeNav, openId, setOpenId }) => {
  const isOpen = openId === id;
  return (
    <li className='cursor-pointer'>
      <div
        onClick={() => setOpenId(isOpen ? null : id)}
        className='flex items-center gap-2 text-[#f1f1f1] font-medium text-[15px] py-1'
      >
        {label}
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </div>
      {isOpen && (
        <div className='mt-1 bg-[#1E1E1E] w-full py-3 px-4 rounded-lg flex flex-col gap-1'>
          {links.map((item, i) => (
            <NavLink
              key={i}
              to={item.path}
              onClick={closeNav}
              className={({ isActive }) =>
                `block py-1.5 text-sm font-medium rounded px-2 transition-colors
                 ${isActive ? 'text-[#4AC3D5]' : 'text-[#f1f1f1] hover:text-[#4AC3D5]'}`
              }
            >
              <span className='text-base pr-2'>•</span>{item.label}
            </NavLink>
          ))}
        </div>
      )}
    </li>
  );
};

const ServicesAccordion = ({ id, closeNav, openId, setOpenId }) => {
  const isOpen = openId === id;
  return (
    <li className='cursor-pointer'>
      <div
        onClick={() => setOpenId(isOpen ? null : id)}
        className='flex items-center gap-2 text-[#f1f1f1] font-medium text-[15px] py-1'
      >
        Services
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </div>
      {isOpen && (
        <div className='mt-1 bg-[#1E1E1E] w-full py-3 px-4 rounded-lg flex flex-col gap-1'>
          
          {/* New Main Services Link Added Here */}
          <NavLink
            to="/services"
            onClick={closeNav}
            className={({ isActive }) =>
              `block py-2 text-sm font-medium rounded px-2 transition-colors border-b border-[#333] mb-2
               ${isActive ? 'text-[#4AC3D5]' : 'text-[#f1f1f1] hover:text-[#4AC3D5]'}`
            }
          >
            <span className='text-base pr-2'>•</span>All Services
          </NavLink>

          <p className='text-xs text-[#4AC3D5] font-semibold uppercase tracking-wider mb-1 px-2'>Core Services</p>
          {SERVICES_CORE.map((item, i) => (
            <NavLink
              key={i}
              to={item.path}
              onClick={closeNav}
              className={({ isActive }) =>
                `block py-1.5 text-sm font-medium rounded px-2 transition-colors
                 ${isActive ? 'text-[#4AC3D5]' : 'text-[#f1f1f1] hover:text-[#4AC3D5]'}`
              }
            >
              <span className='text-base pr-2'>•</span>{item.label}
            </NavLink>
          ))}
        </div>
      )}
    </li>
  );
};



const HifahLearningButton = ({ className = '' }) => (
  <motion.a
    href="http://learning.hifahtechnologies.com/"
    target="_blank"
    rel="noopener noreferrer"
    className={`relative inline-flex items-center gap-2 overflow-hidden rounded-full
                px-4 xl:px-5 py-2.5 text-white font-medium text-[13px] xl:text-sm
                whitespace-nowrap transition-transform duration-200
                hover:scale-[1.05] active:scale-[0.98]
                ${className}`}
    style={{ background: 'linear-gradient(0deg, #FF9D37 19%, #FFCB4B 57.5%)' }}
    animate={{
      scale: [1, 1.035, 1],
      boxShadow: [
        '0 0 0 0 rgba(255,157,55,0)',
        '0 4px 26px 4px rgba(255,180,60,0.9)',
        '0 0 0 0 rgba(255,157,55,0)',
      ],
    }}
    transition={{
      duration: 2.2,
      repeat: Infinity,
      repeatDelay: 0.6,
      ease: 'easeInOut',
    }}
  >
    {/* Soft pulsing halo behind the button */}
    <motion.span
      aria-hidden="true"
      className="pointer-events-none absolute -inset-1.5 rounded-full -z-10"
      style={{ background: 'linear-gradient(0deg, #FF9D37, #FFCB4B)' }}
      animate={{ opacity: [0, 0.5, 0], scale: [0.9, 1.25, 0.9] }}
      transition={{
        duration: 2.2,
        repeat: Infinity,
        repeatDelay: 0.6,
        ease: 'easeInOut',
      }}
    />

    <svg
      width="19" height="17" viewBox="0 0 19 17" fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="relative z-10 w-4 h-4 xl:w-[19px] xl:h-[17px] flex-shrink-0"
    >
      <path
        d="M13.3516 9.96709V7.46245C13.3516 7.31289 13.3516 7.2381 13.3288 7.17208C13.3087 7.11369 13.2758 7.06052 13.2326 7.01639C13.1837 6.9665 13.1168 6.93305 12.983 6.86617L9.1849 4.96709M2.51823 5.80043V11.4726C2.51823 11.7825 2.51823 11.9375 2.56658 12.0732C2.60932 12.1931 2.67898 12.3017 2.77021 12.3905C2.87341 12.491 3.01429 12.5556 3.29602 12.6847L8.62936 15.1291C8.83372 15.2228 8.9359 15.2696 9.04235 15.2881C9.13667 15.3045 9.23312 15.3045 9.32745 15.2881C9.43389 15.2696 9.53607 15.2228 9.74044 15.1291L15.0738 12.6847C15.3555 12.5556 15.4964 12.491 15.5996 12.3905C15.6908 12.3017 15.7605 12.1931 15.8032 12.0732C15.8516 11.9375 15.8516 11.7825 15.8516 11.4726V5.80043M0.851562 4.96709L8.88675 0.9495C8.99607 0.894841 9.05073 0.867511 9.10806 0.856754C9.15884 0.847228 9.21095 0.847228 9.26173 0.856754C9.31906 0.867511 9.37372 0.894841 9.48304 0.9495L17.5182 4.96709L9.48304 8.98469C9.37372 9.03935 9.31906 9.06668 9.26173 9.07744C9.21095 9.08696 9.15884 9.08696 9.10806 9.07744C9.05073 9.06668 8.99607 9.03935 8.88675 8.98469L0.851562 4.96709Z"
        stroke="white" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>

    <span className="relative z-10">Hifah Learning</span>

    {/* Bright shimmer sweep */}
    <motion.span
      aria-hidden="true"
      className="pointer-events-none absolute inset-y-0 left-0 w-1/3 skew-x-[-20deg]"
      style={{
        background:
          'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.9) 50%, rgba(255,255,255,0) 100%)',
      }}
      animate={{ x: ['-150%', '350%'] }}
      transition={{
        duration: 1.3,
        repeat: Infinity,
        repeatDelay: 1.2,
        ease: 'easeInOut',
      }}
    />
  </motion.a>
);






const Navbar = ({ showForm, setShowForm }) => {
  const [navLinks, setNavLinks] = useState(false);
  const navigate = useNavigate();

  const [open, setOpen]             = useState(false);
  const [openAbout, setOpenAbout]   = useState(false);
  const [openCareer, setOpenCareer] = useState(false);
  const [openStaff, setOpenStaff]   = useState(false);

  const timeoutRef = useRef(null);
  const aboutRef   = useRef(null);
  const careerRef  = useRef(null);
  const staffRef   = useRef(null);

  const [mobileOpenId, setMobileOpenId] = useState(null);

  const path = useLocation();
  useEffect(() => {
    setOpenCareer(false);
    setOpenAbout(false);
    setOpen(false);
    setNavLinks(false);
    setMobileOpenId(null);
  }, [path.pathname]);

  const closeNav = () => {
    setNavLinks(false);
    setMobileOpenId(null);
  };

  const makeHover = (setter, ref) => ({
    onMouseEnter: () => { if (ref.current) clearTimeout(ref.current); setter(true); },
    onMouseLeave: () => { ref.current = setTimeout(() => setter(false), 300); },
  });

  const [showNav, setShowNav] = useState(true);
  const lastY = useRef(0);
  useEffect(() => {
    const onScroll = () => {
      const y = Math.max(0, window.scrollY);
      const dy = y - lastY.current;
      if (dy > 3 && y > 80) setShowNav(false);
      if (dy < -3) setShowNav(true);
      lastY.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const [isHero, setIsHero] = useState(true);
  useEffect(() => {
    const handleScroll = () => setIsHero(window.scrollY < 100);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when sidebar is open
  useEffect(() => {
    document.body.style.overflow = navLinks ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [navLinks]);

  const router = useLocation();
  const isHome = router.pathname === '/' || router.pathname === '/staff-augmentation';

  // Sidebar rendered via portal directly into document.body
  // so it escapes any parent z-index stacking context
  const sidebar = (
    <AnimatePresence>
      {navLinks && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          style={{ position: 'fixed', inset: 0, zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          {/* Backdrop */}
          <div style={{ position: 'absolute', inset: 0 }} onClick={closeNav} />

          {/* Sidebar panel */}
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            style={{
              position: 'relative',
              width: '75%',
              maxWidth: '320px',
              height: '100%',
              backgroundColor: '#121212',
              borderRadius: '0 1rem 1rem 0',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Header — never scrolls */}
            <div className='flex justify-between items-center p-6 flex-shrink-0'>
              <img
                onClick={() => { navigate('/'); closeNav(); }}
                className='w-24 cursor-pointer'
                src={logo}
                alt="logo"
              />
              <button onClick={closeNav} className='text-white cursor-pointer'>
                <X className='w-6 h-6' />
              </button>
            </div>

            {/* Scrollable nav area */}
            <nav className='flex-1 overflow-y-auto px-6 pb-8'>
              <ul className='flex flex-col gap-5 font-medium'>

                <li>
                  <NavLink
                    to="/"
                    onClick={closeNav}
                    className={({ isActive }) =>
                      `text-[15px] font-medium ${isActive ? 'text-[#4AC3D5]' : 'text-[#f1f1f1]'}`
                    }
                  >Home</NavLink>
                </li>

                <MobileAccordion
                  id="about"
                  label="About Us"
                  links={ABOUT_LINKS}
                  closeNav={closeNav}
                  openId={mobileOpenId}
                  setOpenId={setMobileOpenId}
                />

                <ServicesAccordion
                  id="services"
                  closeNav={closeNav}
                  openId={mobileOpenId}
                  setOpenId={setMobileOpenId}
                />

                <li>
                  <NavLink
                    to="/portfolio"
                    onClick={closeNav}
                    className={({ isActive }) =>
                      `text-[15px] font-medium ${isActive ? 'text-[#4AC3D5]' : 'text-[#f1f1f1]'}`
                    }
                  >Portfolio</NavLink>
                </li>

                <MobileAccordion
                  id="careers"
                  label="Careers"
                  links={CAREERS_LINKS}
                  closeNav={closeNav}
                  openId={mobileOpenId}
                  setOpenId={setMobileOpenId}
                />

                <MobileAccordion
                  id="staff"
                  label="Staff Augmentation"
                  links={STAFF_LINKS}
                  closeNav={closeNav}
                  openId={mobileOpenId}
                  setOpenId={setMobileOpenId}
                />

                <li>
                  <HifahLearningButton className="w-full justify-center py-2.5" />
                </li>

                <li>
                  <button
                    onClick={() => { navigate('/contact'); closeNav(); }}
                    className='w-full text-white font-medium text-sm bg-[#4AC3D5] py-2.5 rounded-full text-center'
                  >
                    Contact Us
                  </button>
                </li>

              </ul>
            </nav>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <>
      <section
        className={`w-full fixed roboto top-0 shadow-[0px_0px_24px_0px_#00000010]
          transition-transform duration-300 will-change-transform z-20
          ${showNav ? 'translate-y-0' : '-translate-y-full'}
          ${isHome && isHero ? 'bg-transparent' : 'bg-[#121212]'}
        `}
      >
        {showForm && (
          <div className='w-full h-screen bg-[#00000080] fixed top-0 left-0 z-50 overflow-y-auto'>
            <div onClick={() => setShowForm(false)} className='w-full h-screen absolute z-[-1]' />
          </div>
        )}

        <div className='w-[90%] xl:w-[88%] m-auto flex justify-between items-center py-2 lg:py-3'>

          {/* Logo */}
          <div className='flex-shrink-0'>
            <img
              onClick={() => navigate('/')}
              className='w-30 cursor-pointer'
              src={logo}
              alt="logo"
            />
          </div>

          {/* Desktop nav */}
          <nav className='hidden lg:block'>
            <ul className='flex items-center gap-3 xl:gap-6 text-[#FFFFFF] font-medium
                           text-[13px] xl:text-[15px] 2xl:text-[16px] whitespace-nowrap'>

              <li className='inline-block py-0 cursor-pointer'>
                <NavLink to="/" className={({ isActive }) =>
                  isActive
                    ? 'text-[#4AC3D5] px-2 py-2 border-b-[2.5px] border-[#4AC3D5] font-medium'
                    : 'text-[#f1f1f1] hover:text-[#4AC3D5]'
                }>Home</NavLink>
              </li>

              <li className='inline-block relative py-0 cursor-pointer' {...makeHover(setOpenAbout, aboutRef)}>
                <NavLink to="/about" className={({ isActive }) =>
                  `flex items-center gap-1 ${isActive
                    ? 'text-[#4AC3D5] px-2 py-2 border-b-[2.5px] border-[#4AC3D5] font-medium'
                    : 'text-[#f1f1f1] hover:text-[#4AC3D5]'}`
                }>About Us <ChevronDown className='w-4 h-4' /></NavLink>
                <AboutDropDown open={openAbout} />
              </li>

              <li className='inline-block py-0 cursor-pointer' {...makeHover(setOpen, timeoutRef)}>
                <NavLink to="/services" className={({ isActive }) =>
                  `flex items-center gap-1 ${isActive
                    ? 'text-[#4AC3D5] px-2 py-2 border-b-[2.5px] border-[#4AC3D5] font-medium'
                    : 'text-[#f1f1f1] hover:text-[#4AC3D5]'}`
                }>Services <ChevronDown className='w-4 h-4' /></NavLink>
                <ServicesDropdown open={open} />
              </li>

              <li className='inline-block py-0 cursor-pointer'>
                <NavLink to="/portfolio" className={({ isActive }) =>
                  isActive
                    ? 'text-[#4AC3D5] px-2 py-2 border-b-[2.5px] border-[#4AC3D5] font-medium'
                    : 'text-[#f1f1f1] hover:text-[#4AC3D5]'
                }>Portfolio</NavLink>
              </li>

              <li className='inline-block py-0 cursor-pointer' {...makeHover(setOpenCareer, careerRef)}>
                <NavLink to="/careers" className={({ isActive }) =>
                  `flex items-center gap-1 ${isActive
                    ? 'text-[#4AC3D5] px-2 py-2 border-b-[2.5px] border-[#4AC3D5] font-medium'
                    : 'text-[#f1f1f1] hover:text-[#4AC3D5]'}`
                }>Careers <ChevronDown className='w-4 h-4' /></NavLink>
                <JoinDropDown open={openCareer} />
              </li>

              <li className='inline-block py-0 cursor-pointer' {...makeHover(setOpenStaff, staffRef)}>
                <NavLink to="/staff-augmentation" className={({ isActive }) =>
                  `flex items-center gap-1 whitespace-nowrap ${isActive
                    ? 'text-[#4AC3D5] px-2 py-2 border-b-[2.5px] border-[#4AC3D5] font-medium'
                    : 'text-[#f1f1f1] hover:text-[#4AC3D5]'}`
                }>Staff Augmentation <ChevronDown className='w-4 h-4' /></NavLink>
                <StaffDropDown open={openStaff} />
              </li>

            </ul>
          </nav>

         {/* Contact button desktop */}
          <div className='hidden lg:flex items-center gap-2.5 xl:gap-3 flex-shrink-0'>
            <HifahLearningButton />
            <button
              onClick={() => navigate('/contact')}
              className='whitespace-nowrap text-white font-medium text-[13px] xl:text-sm
                         bg-[#4AC3D5] py-2.5 px-4 xl:px-5 rounded-full cursor-pointer'
            >
              Contact Us
            </button>
          </div>
          {/* Burger — mobile/tablet */}
          <div className='block lg:hidden'>
            <img
              onClick={() => setNavLinks(true)}
              src={menu}
              className='w-9 h-9 cursor-pointer'
              alt="menu"
            />
          </div>

        </div>
      </section>

      {/* Sidebar portal — renders directly into document.body, escapes all z-index stacking */}
      {createPortal(sidebar, document.body)}
    </>
  );
};

export default Navbar;